/**
 * Condo Documents Agent: the Wis. Stat. § 703.33 condominium document package.
 *
 * The seller builds a package (one upload or "not applicable" per checklist
 * item), attests that it is complete and delivers it. The buyer acknowledges
 * receipt of everything, or reports missing items. The buyer's 5-business-day
 * cancellation window runs from delivery to the buyer's account when the
 * buyer's E-SIGN consent is on file and the email notice was accepted;
 * otherwise from the buyer's acknowledgement (`deadline.anchor_source`). The
 * server computes and stores the deadline (fail-closed calendar); the client
 * only displays it.
 *
 * Contract: docs/architecture/paperwork-categories.md §8.5.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import APIAgent, { API_BASE_URL } from './apiAgent';

const base = (transactionId) => `/api/transactions/${transactionId}/condo-documents`;

/** Uploads are up to 25 MB, so they outrun the 10s default on slow links. */
const UPLOAD_TIMEOUT_MS = 120000;

export const PACKAGE_KIND_INITIAL = 'initial';
export const PACKAGE_KIND_SUPPLEMENT = 'supplement';
export const PACKAGE_KIND_AMENDMENT = 'amendment';

export const PACKAGE_STATUS_DRAFT = 'draft';
export const PACKAGE_STATUS_DELIVERED = 'delivered';
export const PACKAGE_STATUS_RECEIVED = 'received';

export const CLOCK_STATUS_NOT_STARTED = 'not_started';
export const CLOCK_STATUS_RUNNING = 'running';
export const CLOCK_STATUS_ENDED = 'ended';
export const CLOCK_STATUS_PENDING_CALENDAR = 'pending_calendar';

/** `clock.not_started_reason` (ClockOut). */
export const NOT_STARTED_NOT_DELIVERED = 'not_delivered';
export const NOT_STARTED_DELIVERY_UNCONFIRMED = 'delivery_unconfirmed';
export const NOT_STARTED_AWAITING_ACK = 'awaiting_acknowledgement';
export const NOT_STARTED_AWAITING_MISSING = 'awaiting_missing_documents';

/** `deadline.anchor_source` (DeadlineOut): which event started a window. */
export const ANCHOR_SOURCE_DELIVERY = 'delivery';
/** Delivery unproven; the buyer first opened or downloaded a file before (or without) acknowledging. */
export const ANCHOR_SOURCE_FIRST_VIEW = 'buyer_first_view';
export const ANCHOR_SOURCE_ACK = 'buyer_acknowledgement';
export const ANCHOR_SOURCE_AFTER_CURE = 'after_cure';

/** `viewer_role` (CondoDocumentsSummaryOut). */
export const VIEWER_ROLE_SELLER = 'seller';
export const VIEWER_ROLE_BUYER = 'buyer';
export const VIEWER_ROLE_OTHER = 'other';

export const CondoDocumentsAPI = {
  /** `CondoDocumentsSummaryOut`: rule, checklist items, packages, clock, warnings. */
  getSummary: (transactionId) => APIAgent.request(`${base(transactionId)}/`, 'GET'),

  /**
   * Seller: start a package. 409 if an `initial` already exists.
   * `smallCondominium` (initial only) answers the small-condominium question;
   * omitted, the server uses the listing's answer, else false.
   */
  createPackage: (transactionId, kind = PACKAGE_KIND_INITIAL, { smallCondominium } = {}) =>
    APIAgent.request(`${base(transactionId)}/packages`, 'POST', {
      kind,
      ...(kind === PACKAGE_KIND_INITIAL && typeof smallCondominium === 'boolean'
        ? { small_condominium: smallCondominium }
        : {}),
    }),

  /** Seller, draft initial package only: answer the small-condominium question. */
  setSmallCondominium: (transactionId, packageId, smallCondominium) =>
    APIAgent.request(
      `${base(transactionId)}/packages/${packageId}/small-condominium`,
      'PUT',
      { small_condominium: smallCondominium === true }
    ),

  /** Seller, draft only: attach one file to a checklist item. */
  uploadFile: (transactionId, packageId, itemCode, file) => {
    const formData = new FormData();
    formData.append('item_code', itemCode);
    formData.append('file', file);
    return APIAgent.request(
      `${base(transactionId)}/packages/${packageId}/files`,
      'POST',
      formData,
      {},
      { timeout: UPLOAD_TIMEOUT_MS }
    );
  },

  /** Seller, draft only. Resolves to `null` (204). */
  removeFile: (transactionId, packageId, fileId) =>
    APIAgent.request(`${base(transactionId)}/packages/${packageId}/files/${fileId}`, 'DELETE'),

  /** Seller, draft only: mark an item N/A (with a note) or clear the mark. */
  setItem: (transactionId, packageId, itemCode, { notApplicable, note } = {}) =>
    APIAgent.request(
      `${base(transactionId)}/packages/${packageId}/items/${encodeURIComponent(itemCode)}`,
      'PUT',
      { not_applicable: Boolean(notApplicable), note: note || null }
    ),

  /**
   * Seller: attest and deliver. 422 `{code: 'condo_package_incomplete',
   * missing_items}` when an item is uncovered; 409 when the package is not a
   * draft or the attestation wording is stale.
   */
  deliver: (transactionId, packageId, { signatureName, certify, attestationVersion }) =>
    APIAgent.request(`${base(transactionId)}/packages/${packageId}/deliver`, 'POST', {
      signature_name: signatureName,
      certify: certify === true,
      attestation_version: attestationVersion,
    }),

  /** Buyer: acknowledge receipt. Resolves to the fresh summary. */
  acknowledge: (
    transactionId,
    packageId,
    { allReceived, missingItems = [], signatureName, certify, attestationVersion }
  ) =>
    APIAgent.request(`${base(transactionId)}/packages/${packageId}/acknowledge`, 'POST', {
      all_received: allReceived === true,
      missing_items: allReceived ? [] : missingItems,
      signature_name: signatureName,
      certify: certify === true,
      attestation_version: attestationVersion,
    }),

  /**
   * The authorized download route, for a plain link. Auth rides on the session
   * cookie, and the server records the buyer's first view.
   */
  downloadUrl: (transactionId, fileId) =>
    `${API_BASE_URL}${base(transactionId)}/files/${fileId}/download`,
};

const isNotFound = (err) => err?.status === 404 || err?.statusCode === 404;
const isConflict = (err) => err?.status === 409 || err?.statusCode === 409;

/**
 * Loads the condo document summary for a transaction and exposes its actions.
 *
 * - `loading` covers the first load only; later reloads keep the panel on screen.
 * - `error` is a load failure. A 404 is not an error: it means the rule doesn't
 *   apply to this transaction, reported as `notApplicable`.
 * - `actionError` is the last failed action, kept separate so a failed upload
 *   never replaces the panel. `actionErrorDetail` carries the structured
 *   `detail` (e.g. the 422 `missing_items`).
 * - Every action resolves to `{ success, data?, error? }` and never throws.
 *
 * @param {number|string} transactionId
 */
export function useCondoDocuments(transactionId) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notApplicable, setNotApplicable] = useState(false);
  const [busy, setBusy] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [actionErrorDetail, setActionErrorDetail] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const load = useCallback(async ({ background = false } = {}) => {
    if (!transactionId) return;
    if (!background) setLoading(true);
    setError(null);
    try {
      const data = await CondoDocumentsAPI.getSummary(transactionId);
      if (!mountedRef.current) return;
      setSummary(data);
      setNotApplicable(false);
    } catch (err) {
      if (!mountedRef.current) return;
      if (isNotFound(err)) {
        setNotApplicable(true);
        setSummary(null);
      } else {
        console.error('Error loading condo documents:', err);
        setError(err.message || 'Failed to load condominium documents');
      }
    } finally {
      if (mountedRef.current && !background) setLoading(false);
    }
  }, [transactionId]);

  useEffect(() => { load(); }, [load]);

  /**
   * Run one mutation: track it as `busy` (a key the UI can match), clear the
   * last action error, and refresh the summary afterwards.
   */
  const run = useCallback(async (busyKey, call, { applySummary = false, reloadOnConflict = false } = {}) => {
    setBusy(busyKey);
    setActionError(null);
    setActionErrorDetail(null);
    try {
      const data = await call();
      if (!mountedRef.current) return { success: true, data };
      if (applySummary && data) {
        setSummary(data);
      } else {
        await load({ background: true });
      }
      return { success: true, data };
    } catch (err) {
      if (mountedRef.current) {
        console.error(`Condo documents action "${busyKey}" failed:`, err);
        setActionError(err.message || 'Something went wrong. Please try again.');
        setActionErrorDetail(err.detail ?? null);
        if (reloadOnConflict && isConflict(err)) {
          await load({ background: true });
        }
      }
      return { success: false, error: err.message, detail: err.detail, status: err.status };
    } finally {
      if (mountedRef.current) setBusy(null);
    }
  }, [load]);

  const createPackage = useCallback(
    (kind = PACKAGE_KIND_INITIAL, options = {}) =>
      run(`create:${kind}`, () => CondoDocumentsAPI.createPackage(transactionId, kind, options), {
        reloadOnConflict: true,
      }),
    [run, transactionId]
  );

  const setSmallCondominium = useCallback(
    (packageId, smallCondominium) =>
      run('small-condominium', () =>
        CondoDocumentsAPI.setSmallCondominium(transactionId, packageId, smallCondominium), {
        reloadOnConflict: true,
      }),
    [run, transactionId]
  );

  const uploadFile = useCallback(
    (packageId, itemCode, file) =>
      run(`upload:${itemCode}`, () =>
        CondoDocumentsAPI.uploadFile(transactionId, packageId, itemCode, file)
      ),
    [run, transactionId]
  );

  const removeFile = useCallback(
    (packageId, fileId) =>
      run(`remove:${fileId}`, () => CondoDocumentsAPI.removeFile(transactionId, packageId, fileId)),
    [run, transactionId]
  );

  const setItem = useCallback(
    (packageId, itemCode, values) =>
      run(`item:${itemCode}`, () =>
        CondoDocumentsAPI.setItem(transactionId, packageId, itemCode, values)
      ),
    [run, transactionId]
  );

  const deliver = useCallback(
    (packageId, attestation) =>
      run('deliver', () => CondoDocumentsAPI.deliver(transactionId, packageId, attestation), {
        reloadOnConflict: true,
      }),
    [run, transactionId]
  );

  const acknowledge = useCallback(
    (packageId, receipt) =>
      run('acknowledge', () => CondoDocumentsAPI.acknowledge(transactionId, packageId, receipt), {
        applySummary: true,
        reloadOnConflict: true,
      }),
    [run, transactionId]
  );

  const clearActionError = useCallback(() => {
    setActionError(null);
    setActionErrorDetail(null);
  }, []);

  return {
    summary,
    loading,
    error,
    notApplicable,
    busy,
    actionError,
    actionErrorDetail,
    clearActionError,
    reload: load,
    createPackage,
    setSmallCondominium,
    uploadFile,
    removeFile,
    setItem,
    deliver,
    acknowledge,
  };
}

export default CondoDocumentsAPI;
